import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/feed";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await signIn(form.email.trim(), form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate(next);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-ink/15 bg-grain">
        <Link to="/" className="inline-flex items-center">
          <img src="/inonit-logo.png" alt="inonit" className="h-9 w-auto select-none" draggable={false} />
        </Link>
        <div>
          <p className="font-display text-4xl xl:text-6xl font-bold leading-[0.95] tracking-tight text-ink">
            Real events.<br />
            Real people.<br />
            <span className="text-ink">No algorithm.</span>
          </p>
          <p className="text-ink/60 mt-6 max-w-md">Welcome back. The feed missed you.</p>
        </div>
        <span className="text-xs uppercase tracking-widest text-ink/40">© inonit</span>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md">
          <span className="text-xs uppercase tracking-[0.3em] text-ink">Log in</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 mb-10 leading-none">
            Hey,<br />welcome back.
          </h1>

          <form onSubmit={submit} data-testid="login-form" className="flex flex-col gap-6">
            <Field label="Email or username">
              <input
                type="text"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email"
                placeholder="you@yourtown.com or @yourhandle"
                className="chunky-input"
                autoCapitalize="none"
                autoComplete="username"
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password"
                placeholder="••••••••"
                className="chunky-input"
              />
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="self-end -mt-1 text-xs uppercase tracking-widest text-ink/55 hover:text-ink underline-offset-4 hover:underline transition-colors"
              >
                Forgot password?
              </Link>
            </Field>

            {error && (
              <div data-testid="login-error" className="text-destructive text-sm border border-destructive/40 px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="bg-neon text-ink py-4 font-bold uppercase tracking-widest hover:bg-paper transition-colors disabled:opacity-60 mt-2"
            >
              {loading ? "Logging in…" : "Log in"}
            </button>

            <div className="text-sm text-ink/60 mt-4 flex items-center gap-2 flex-wrap">
              <span>No account?</span>
              <Link
                to="/signup"
                data-testid="link-signup"
                className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-ink underline underline-offset-4 decoration-[3px] decoration-lime hover:decoration-ink transition-colors"
              >
                Create account
              </Link>
            </div>
          </form>

          <style>{`
            .bare-input {
              background: transparent; border: none;
              border-bottom: 1px solid hsl(var(--border));
              color: #f4f4f0; padding: 12px 0; width: 100%;
              font-family: inherit; font-size: 1rem; outline: none;
              transition: border-color 200ms;
            }
            .bare-input:focus { border-bottom-color: #d6ff00; border-bottom-width: 2px; }
            .bare-input::placeholder { color: rgba(244,244,240,0.35); }
          `}</style>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-ink/60">{label}</span>
      {children}
    </label>
  );
}
